import React, { useState, useEffect } from 'react';
import MetaCostIncreaseForm from './MetaCostIncreaseForm';
import MetaCostIncreaseList from './MetaCostIncreaseList';
import MetaCostIncreaseHistory from './MetaCostIncreaseHistory';
import MetaCostIncreaseQuickActionModal from './MetaCostIncreaseQuickActionModal';
import { useNotifications } from '../contexts/NotificationContext';
import { loadMetaCostIncrease } from '../api';

const MetaCostIncreaseSection = ({ currentUser, permissions }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [showQuickAction, setShowQuickAction] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const { showNotification } = useNotifications();

  useEffect(() => {
    loadRecords();
  }, []);

  const loadRecords = async () => {
    try {
      setLoading(true);
      
      // Pasar las células del usuario si requiere filtrado por células
      const userCells = currentUser && currentUser.role !== 'admin' && currentUser.cells ? currentUser.cells : null;
      const userRole = currentUser ? currentUser.role : null;
      console.log('🔍 Meta Cost IncreaseSection - Cargando con células del usuario:', userCells);
      console.log('🔍 Meta Cost IncreaseSection - Rol del usuario:', userRole);
      
      const recordsData = await loadMetaCostIncrease(userCells, userRole);
      setRecords(recordsData || []);
    } catch (error) {
      console.error('Error loading meta cost increase:', error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    try {
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const url = editingRecord 
        ? `${API_URL}/meta-cost-increase/${editingRecord.id}`
        : `${API_URL}/meta-cost-increase`;
      
      const method = editingRecord ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        showNotification(
          editingRecord 
            ? 'Registro de aumento de costo actualizado exitosamente' 
            : 'Registro de aumento de costo creado exitosamente', 
          'success'
        );
        
        // Recargar la lista
        await loadRecords();
        
        // Cerrar el formulario
        setShowForm(false);
        setEditingRecord(null);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error ${response.status}`);
      }
    } catch (error) {
      console.error('Error al guardar:', error);
      showNotification('Error al guardar el registro: ' + error.message, 'error');
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este registro?')) {
      return;
    }

    try {
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/meta-cost-increase/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        showNotification('Registro eliminado exitosamente', 'success');
        await loadRecords();
      } else {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error al eliminar:', error);
      showNotification('Error al eliminar el registro: ' + error.message, 'error');
    }
  };

  const handleShowHistory = (record) => {
    setSelectedRecordId(record.id);
    setShowHistory(true);
  };

  const handleQuickAction = (record) => {
    setSelectedRecord(record);
    setShowQuickAction(true);
  };

  const handleQuickActionSave = async (recordId, actionData) => {
    try {
      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      // Registrar la acción en el historial
      const historyResponse = await fetch(`${API_URL}/meta-cost-increase/${recordId}/history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(actionData),
      });

      if (!historyResponse.ok) {
        throw new Error('Error al registrar en el historial');
      }

      // Si la acción incluye cambio de estado, actualizar el registro
      if (actionData.new_status) {
        const updateResponse = await fetch(`${API_URL}/meta-cost-increase/${recordId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: actionData.new_status }),
        });

        if (!updateResponse.ok) {
          throw new Error('Error al actualizar el estado');
        }
      }

      showNotification('Acción registrada exitosamente', 'success');
      await loadRecords();
      
    } catch (error) {
      console.error('Error en acción rápida:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        <span className="ml-3 text-lg text-gray-600">Cargando aumentos de costo...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-yellow-600 rounded-2xl shadow-xl p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold flex items-center">
              <span className="mr-4">💰</span>
              Aumento de Costo por Mensaje Meta
            </h2>
            <p className="text-orange-100 mt-2 text-lg">
              Gestión de aumentos en el costo por mensaje en Meta
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{records.length}</div>
            <div className="text-orange-100">Registros</div>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowForm(true)}
          className="bg-gradient-to-r from-orange-600 to-yellow-600 text-white px-6 py-3 rounded-xl hover:from-orange-700 hover:to-yellow-700 font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center"
        >
          <span className="mr-2">➕</span>
          Nuevo Registro
        </button>
      </div>

      {/* Records List */}
      <MetaCostIncreaseList
        records={records}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onShowHistory={handleShowHistory}
        onQuickAction={handleQuickAction}
        currentUser={currentUser}
        permissions={permissions}
      />

      {/* Form Modal */}
      {showForm && (
        <MetaCostIncreaseForm
          record={editingRecord}
          onSave={handleSave}
          onClose={() => {
            setShowForm(false);
            setEditingRecord(null);
          }}
          currentUser={currentUser}
        />
      )}

      {/* History Modal */}
      {showHistory && (
        <MetaCostIncreaseHistory
          recordId={selectedRecordId}
          onClose={() => {
            setShowHistory(false);
            setSelectedRecordId(null);
          }}
        />
      )}

      {/* Quick Action Modal */}
      {showQuickAction && (
        <MetaCostIncreaseQuickActionModal
          record={selectedRecord}
          onClose={() => {
            setShowQuickAction(false);
            setSelectedRecord(null);
          }}
          onSave={handleQuickActionSave}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default MetaCostIncreaseSection;
